/**
 * TextFieldR — wraps the existing TextField component with schema-aware props.
 * Handles types: text, passport_number, phone, email.
 */

import { useFormContext } from 'react-hook-form';
import { TextField } from '../../../components/intake/fields';
import type { FieldDef, RendererProps } from '../types';
import { buildValidationRules } from '../validation/fromSchema';
import { resolveLabel, resolveHelpText } from '../utils';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
  /** Override the input type attribute (e.g. "email", "tel") */
  inputType?: string;
}

export function TextFieldR({ field, formId, pdfMode = false, namePrefix, audience, inputType }: Props) {
  const { getValues } = useFormContext();
  const name = namePrefix ? `${namePrefix}.${field.canonical_name.split('.').pop()!}` : field.canonical_name;
  const label = resolveLabel(field, formId, audience);
  const rules = buildValidationRules(field);

  if (pdfMode) {
    const value = getValues(name);
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">{value ?? <span className="text-slate-400 italic">Not provided</span>}</p>
      </div>
    );
  }

  return (
    <TextField
      name={name}
      label={label}
      hint={resolveHelpText(field, audience)}
      required={field.required}
      rules={rules}
      type={inputType ?? 'text'}
      placeholder={field.example ? `e.g. ${field.example}` : undefined}
      tooltip={lookupFieldTooltip(field.canonical_name)}
    />
  );
}

// Register with react-hook-form using validation rules — the base TextField
// only calls register(name) without rules, so we need to register separately.
// To avoid double-registration we export a hook that components can use.
export function useRegisterField(
  field: FieldDef,
  namePrefix: string | undefined,
) {
  const { register } = useFormContext();
  const name = namePrefix
    ? `${namePrefix}.${field.canonical_name.split('.').pop()!}`
    : field.canonical_name;
  return register(name, buildValidationRules(field));
}
